import React from 'react';
import { ShieldCheck, Clock, Award, Sparkles, Truck, Droplets } from 'lucide-react';

const About = () => {
  const values = [
    { icon: Truck, title: 'We Come To You', description: 'Home, office, or anywhere in Sydney — we bring the studio to you.' },
    { icon: Award, title: 'Premium Products', description: 'Professional-grade, vehicle-safe chemicals and microfibre.' },
    { icon: ShieldCheck, title: '100% Guarantee', description: 'Not happy? We make it right. Every job, every time.' },
    { icon: Clock, title: 'On-Time, Every Time', description: 'Punctual arrivals and clean handovers — no surprises.' }
  ];

  return (
    <section id="about" className="py-24 bg-white relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, #0ea5e9 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }}></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-20 max-w-3xl mx-auto">
          <div className="inline-flex items-center px-4 py-1.5 bg-blue-50 border border-blue-100 rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-blue-600 mr-2" />
            <span className="text-sm font-semibold text-blue-700">Why ProClean</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 mb-6 leading-tight">
            Detailing reimagined for{' '}
            <span className="relative inline-block">
              <span className="relative z-10 text-blue-600">your driveway</span>
              <span className="absolute bottom-2 left-0 right-0 h-3 bg-cyan-200/60 -z-0"></span>
            </span>
          </h2>
          <p className="text-lg sm:text-xl text-slate-600 leading-relaxed">
            Premium care without the inconvenience. Our team arrives with pro-grade products and equipment, leaving your car looking better than the day you drove it home.
          </p>
        </div>

        {/* Stats + Image */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-24">
          <div className="relative">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden shadow-2xl">
              <img src="/images/gallery1.jpg" alt="Premium detailing" className="w-full h-full object-cover" />
            </div>
            {/* Floating stats card */}
            <div className="absolute -bottom-8 -right-8 bg-white rounded-2xl shadow-2xl p-6 border border-slate-100 hidden sm:block">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center">
                  <Droplets className="w-7 h-7 text-white" />
                </div>
                <div>
                  <div className="text-3xl font-extrabold text-slate-900">500+</div>
                  <div className="text-sm text-slate-500 font-medium">Happy customers</div>
                </div>
              </div>
            </div>
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-full blur-3xl opacity-30 -z-10"></div>
          </div>

          <div className="space-y-8">
            <h3 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
              The detailing studio,<br />delivered to your door.
            </h3>
            <p className="text-lg text-slate-600 leading-relaxed">
              At ProClean we believe a great detail shouldn't cost you a Saturday. We come to your home or office and take care of everything — you just hand over the keys.
            </p>
            <p className="text-lg text-slate-600 leading-relaxed">
              From quick refreshes to ceramic-coated showroom finishes, every package is crafted to make your car look — and feel — brand new again.
            </p>
            <div className="flex flex-wrap gap-6 pt-2">
              <div>
                <div className="text-4xl font-extrabold text-slate-900">3+</div>
                <div className="text-sm text-slate-500 font-semibold mt-1">Years experience</div>
              </div>
              <div className="w-px bg-slate-200"></div>
              <div>
                <div className="text-4xl font-extrabold text-slate-900">5.0★</div>
                <div className="text-sm text-slate-500 font-semibold mt-1">Average rating</div>
              </div>
              <div className="w-px bg-slate-200"></div>
              <div>
                <div className="text-4xl font-extrabold text-slate-900">100%</div>
                <div className="text-sm text-slate-500 font-semibold mt-1">Mobile service</div>
              </div>
            </div>
          </div>
        </div>

        {/* Values Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((value, index) => {
            const Icon = value.icon;
            return (
              <div
                key={index}
                className="group bg-gradient-to-br from-slate-50 to-white p-7 rounded-3xl border border-slate-100 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-100/50 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-300">
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <h4 className="font-bold text-slate-900 mb-2 text-lg">{value.title}</h4>
                <p className="text-slate-600 text-sm leading-relaxed">{value.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default About;
